// src/components/programs/ProgramsCatalog.js
// ──────────────────────────────────────────────────────────────────────────
// The centerpiece. Fetches programs (with sample-data fallback), lets users
// search and filter by faculty + level, and renders a responsive card grid.
// ──────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import ProgramsAPI, { FACULTIES, LEVELS } from '../../api/programs';
import theme from '../../theme/theme';

const Section = styled.section`
  max-width: ${theme.layout.maxWidth};
  margin: 0 auto;
  padding: 4rem ${theme.layout.gutter} 6rem;
`;

const Head = styled.header`
  margin-bottom: 2.5rem;
  h1 { font-size: clamp(2.4rem, 6vw, 4rem); }
  p  { color: ${theme.color.inkSoft}; max-width: 46ch; margin-top: 0.8rem; }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 2.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid ${theme.color.line};
`;

const Search = styled.input`
  flex: 1 1 220px;
  font-family: ${theme.font.body};
  font-size: 0.95rem;
  padding: 11px 16px;
  border: 1px solid ${theme.color.line};
  border-radius: ${theme.layout.radius};
  background: #fff;
  color: ${theme.color.ink};
  &:focus { outline: none; border-color: ${theme.color.accent}; }
`;

const Select = styled.select`
  font-family: ${theme.font.body};
  font-size: 0.95rem;
  padding: 11px 14px;
  border: 1px solid ${theme.color.line};
  border-radius: ${theme.layout.radius};
  background: #fff;
  color: ${theme.color.ink};
  cursor: pointer;
  &:focus { outline: none; border-color: ${theme.color.accent}; }
`;

const Count = styled.div`
  width: 100%;
  font-size: 0.85rem;
  color: ${theme.color.inkSoft};
  margin-top: 0.25rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const Card = styled(motion(Link))`
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid ${theme.color.line};
  border-radius: ${theme.layout.radius};
  overflow: hidden;
  transition: transform 0.35s ${theme.ease}, box-shadow 0.35s ${theme.ease};
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 18px 40px -22px rgba(0,0,0,0.35);
  }
`;

const Thumb = styled.div`
  aspect-ratio: 16 / 10;
  background: ${theme.color.paperAlt} url(${(p) => p.$src}) center/cover no-repeat;
`;

const Body = styled.div`
  padding: 1.3rem 1.4rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  flex: 1;
`;

const Tags = styled.div`
  display: flex;
  gap: 0.5rem;
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${theme.color.accent};
  font-weight: 600;
`;

const CardTitle = styled.h3`
  font-size: 1.35rem;
  line-height: 1.15;
`;

const Summary = styled.p`
  font-size: 0.92rem;
  color: ${theme.color.inkSoft};
  margin: 0;
  flex: 1;
`;

const Meta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.8rem;
  border-top: 1px solid ${theme.color.line};
  font-size: 0.82rem;
  color: ${theme.color.inkSoft};
  span:last-child { color: ${theme.color.accent}; font-weight: 600; }
`;

const Empty = styled.div`
  padding: 4rem 0;
  text-align: center;
  color: ${theme.color.inkSoft};
`;

const ProgramsCatalog = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [faculty, setFaculty] = useState('');
  const [level, setLevel] = useState('');

  useEffect(() => {
    let alive = true;
    ProgramsAPI.getAll({ limit: 100 })
      .then((data) => { if (alive) setPrograms(data.programs || []); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return programs.filter((p) => {
      if (faculty && p.faculty !== faculty) return false;
      if (level && p.level !== level) return false;
      if (q && !(`${p.title} ${p.summary} ${p.faculty}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [programs, search, faculty, level]);

  return (
    <Section>
      <Head>
        <h1>Courses</h1>
        <p>Hands-on skills courses across our four departments — Agriculture, Engineering, Hospitality and ICT.</p>
      </Head>

      <Controls>
        <Search
          placeholder="Search programs…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select value={faculty} onChange={(e) => setFaculty(e.target.value)}>
          <option value="">All departments</option>
          {FACULTIES.map((f) => <option key={f} value={f}>{f}</option>)}
        </Select>
        <Select value={level} onChange={(e) => setLevel(e.target.value)}>
          <option value="">All levels</option>
          {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
        </Select>
        <Count>
          {loading ? 'Loading programs…' : `${filtered.length} program${filtered.length === 1 ? '' : 's'}`}
        </Count>
      </Controls>

      {!loading && filtered.length === 0 ? (
        <Empty>No programs match your filters. Try clearing them.</Empty>
      ) : (
        <Grid>
          {filtered.map((p, i) => (
            <Card
              key={p.id}
              to={`/programs/${p.id}`}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.4), ease: theme.ease }}
            >
              <Thumb $src={p.image} />
              <Body>
                <Tags>
                  <span>{p.level}</span>
                  <span>·</span>
                  <span>{p.faculty}</span>
                </Tags>
                <CardTitle>{p.title}</CardTitle>
                <Summary>{p.summary}</Summary>
                <Meta>
                  <span>{p.duration} · {p.mode}</span>
                  <span>View →</span>
                </Meta>
              </Body>
            </Card>
          ))}
        </Grid>
      )}
    </Section>
  );
};

export default ProgramsCatalog;
